import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppRoutes from 'constants/AppRoutes';
import { SEARCH_PARAMS } from 'constants/dataSets';
import { GEO_TYPE_PREFIX, ORG_TYPE_PREFIX, SHOW_ARCHIVED } from 'constants/formFields';
import { USER_PILLAR_MODULES } from 'constants/interfaces';
import useDataSetListActions from 'hooks/useDataSetListActions';
import useDownloadExample from 'hooks/useDownloadExample';
import useImportErrorModal from 'hooks/useImportErrorModal';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import {
  useGetNaceCodeTreeQuery,
  useLazyOwnOperationExampleQuery,
  useLazyOwnOperationListQuery,
  useOwnOperationArchiveMutation,
  useOwnOperationDeArchiveMutation,
  useOwnOperationImportRecordsMutation,
  useOwnOperationListQuery,
  useOwnOperationsRecordDeleteMutation,
  useProfitCenterListQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';

import useDataSetsListForm from '../ProfitCentres/hooks/useDataSetsListForm';
import OwnOperationsView from './OwnOperationsView';

import type IOwnItem from 'store/slices/dataSets/interfaces/IOwnItem';
import type { IBaseQueryPaginationResponse } from 'store/types/IBaseQueryPaginationResponse';
import type IArchiveError from '../ProfitCentres/interfaces/IArchiveError';
import type { TUseOwnOperationImportRecordsMutation } from './interfaces/TUseOwnOperationImportRecordsMutation';

function OwnOperations() {
  const navigate = useNavigate();
  const {
    isActionsVisible,
    isLoading: isPillarModuleLoading,
  } = usePillarModulePermission(
    { pillarModuleKey: USER_PILLAR_MODULES.settings.dataSetsManagement },
  );

  const [getOwnOperationSheetExample, {
    isLoading: isLazyOwnOperationExampleLoading,
  }] = useLazyOwnOperationExampleQuery();
  const { data: naceCodeTreeData, isLoading: isNaceCodeLoading } = useGetNaceCodeTreeQuery();

  const {
    control, getValues, setValue, watch, handleSubmit,
  } = useDataSetsListForm();
  const [orderBy, setOrderBy] = useState('');
  const [searchParams] = useSearchParams();
  const [isApplyFilter, setIsApplyFilter] = useState(false);
  const [isFiltersBarVisible, setIsFiltersBarVisible] = useState(false);
  const [hasTriedArchived, setHasTriedArchived] = useState(false);

  const [listDataLazy, setListData] = useState <IBaseQueryPaginationResponse<IOwnItem[]>>({
    data: {
      items: [],
      meta: {
        count: 0,
        limit: 0,
        offset: 0,
        hasNext: false,
      },
    },
  });

  const isArchived = watch(SHOW_ARCHIVED);
  const orgTypePrefix = watch(ORG_TYPE_PREFIX);

  const geoTypePrefix = watch(GEO_TYPE_PREFIX);

  const searchParam = searchParams.get(SEARCH_PARAMS.search) || '';
  const offset = Number(searchParams.get(SEARCH_PARAMS.offset) || 0);

  const isWithSearch = !!searchParam || !!orgTypePrefix || !!geoTypePrefix;

  useEffect(() => {
    const isArchivedParam = searchParams.get(SEARCH_PARAMS.isArchived);
    const isArchivedFromUrl = isArchivedParam !== null ? Boolean(isArchivedParam === 'true') : false;

    if (isArchivedFromUrl !== isArchived) {
      setValue(SHOW_ARCHIVED, isArchivedFromUrl);
    }
  }, [searchParams, isArchived, setValue]);

  const { data, isFetching: isOwnOperationListLoading } = useOwnOperationListQuery({
    orderBy,
    keyword: searchParam,
    archived: isArchived,
    offset,
  });

  const [ownOperationList, { isFetching: isLazyOwnOperationListLoading }] = useLazyOwnOperationListQuery();

  const { data: profitCenterData, isLoading: isProfitCenterListLoading } = useProfitCenterListQuery({});

  const [importRecords, { isLoading: isOwnOperationImportLoading }] = useOwnOperationImportRecordsMutation();

  const { data: locationsCountries, isFetching: isLocationsCountriesLoading } = useGetLocationsCountriesQuery();

  const {
    isImportErrorModalVisible,
    importErrorMessage,
    handleImportError,
    handleCloseImportErrorModal,
  } = useImportErrorModal();

  const {
    uploadControl, isMappingModalVisible, onCloseMappingModal, isImportErrorsModalVisible,
    setIsImportErrorsModalVisible,
  } = useDownloadExample<TUseOwnOperationImportRecordsMutation>({
    importRecords,
    onError: handleImportError,
  });

  const listData = isApplyFilter ? listDataLazy : data;

  const isEmptyList = !listData?.data?.meta?.count || listData?.data?.items?.length === 0;

  const { items, meta } = listData?.data || {};

  const [deArchive,
    { isLoading: isOwnOperationDeArchiveLoading, error: deArchiveError }] = useOwnOperationDeArchiveMutation();

  const [archive, { isLoading: isOwnOperationArchiveLoading, error: archiveError }] = useOwnOperationArchiveMutation();

  const [deleteRecord, {
    isLoading: isOwnOperationDeleteLoading,
    error: deleteError,
  }] = useOwnOperationsRecordDeleteMutation();

  const isLoading = isOwnOperationListLoading || isOwnOperationImportLoading || isOwnOperationDeArchiveLoading
      || isOwnOperationArchiveLoading || isOwnOperationDeleteLoading || isLazyOwnOperationListLoading
      || isLazyOwnOperationExampleLoading || isNaceCodeLoading || isProfitCenterListLoading || isPillarModuleLoading
      || isOwnOperationImportLoading || isLocationsCountriesLoading;

  const {
    isArchiveModalVisible,
    isArchiveModalInfoVisible,
    latestArchivation,
    isLineMark,
    onSortChange,
    onSearchChange,
    onSelectAll,
    onArchiveConfirm,
    onArchive,
    onArchiveModalInfoOpen,
    onMoveToArchive,
    onDeArchive,
    onPageChange,
    isArchiveSelectedItemsVisible,
    setIsArchiveModalVisible,
    setIsArchiveModalInfoVisible,
    selectedItems,
    onShowArchiveChange,
    onOpenRestrictedModal,
    activeRestrictedModal,
    onDeleteRecord,
    isDeleteModalVisible,
    onCloseDeleteModal,
    onDeleteConfirm,
  } = useDataSetListActions<IOwnItem[]>({
    items,
    archiveAction: archive,
    deArchiveAction: deArchive,
    deleteAction: deleteRecord,
    setOrderBy,
    getValues,
    setValue,
    watch,
  });

  const isProfitCenterEmpty = !profitCenterData?.data?.meta?.count && isActionsVisible;

  const isUploadFileVisible = isActionsVisible && !isProfitCenterEmpty;

  const currentTotalItems = items?.length || 0;

  const onDownloadExample = () => {
    getOwnOperationSheetExample();
  };

  const onEditRecord = (recordId: number) => {
    navigate(`${AppRoutes.balanceSheetAssetsEdit.replace(':recordId', String(recordId))}`);
  };

  const onAddRecord = () => {
    navigate(AppRoutes.balanceSheetAssetsCreate);
  };

  const onFilterApply = handleSubmit(() => {
    ownOperationList({
      orderBy,
      keyword: searchParam,
      archived: isArchived,
      offset,
      orgTypePrefix,
      geoTypePrefix,
    }).then((response) => {
      if (response.data) {
        setListData(response.data);
        setIsApplyFilter(true);
      }
    });
  });

  const onFilterReset = () => {
    setValue(ORG_TYPE_PREFIX, '');
    setValue(GEO_TYPE_PREFIX, '');
    ownOperationList({
      orderBy,
      keyword: searchParam,
      archived: isArchived,
      offset,
      orgTypePrefix: '',
      geoTypePrefix: '',
    }).then((response) => {
      if (response.data) {
        setListData(response.data);
        setIsApplyFilter(false);
      }
    });
  };

  useEffect(() => {
    const isArchivedParam = searchParams.get(SEARCH_PARAMS.isArchived);
    if (isEmptyList && !isLoading && !isArchivedParam && !hasTriedArchived) {
      setHasTriedArchived(true);
      onShowArchiveChange(true);
    }
  }, [isEmptyList, isLoading, searchParams, onShowArchiveChange, hasTriedArchived]);

  return (
    <OwnOperationsView
      isEmptyList={isEmptyList}
      uploadControl={uploadControl}
      isUploadFileVisible={isUploadFileVisible}
      isProfitCenterEmpty={isProfitCenterEmpty}
      isMappingModalVisible={isMappingModalVisible}
      setIsImportErrorsModalVisible={setIsImportErrorsModalVisible}
      onCloseMappingModal={onCloseMappingModal}
      isImportErrorsModalVisible={isImportErrorsModalVisible}
      onDownloadExample={onDownloadExample}
      importRecords={importRecords}
      control={control}
      isArchiveModalVisible={isArchiveModalVisible}
      isArchiveModalInfoVisible={isArchiveModalInfoVisible}
      latestArchivation={latestArchivation}
      isLineMark={isLineMark}
      onSortChange={onSortChange}
      onSearchChange={onSearchChange}
      onSelectAll={onSelectAll}
      onArchiveConfirm={onArchiveConfirm}
      onArchive={onArchive}
      onArchiveModalInfoOpen={onArchiveModalInfoOpen}
      onMoveToArchive={onMoveToArchive}
      onDeArchive={onDeArchive}
      onPageChange={onPageChange}
      isArchiveSelectedItemsVisible={isArchiveSelectedItemsVisible}
      setIsArchiveModalVisible={setIsArchiveModalVisible}
      setIsArchiveModalInfoVisible={setIsArchiveModalInfoVisible}
      isActionsVisible={isActionsVisible}
      items={items}
      selectedItems={selectedItems}
      currentTotalItems={currentTotalItems}
      onEditRecord={onEditRecord}
      onAddRecord={onAddRecord}
      meta={meta}
      onFilterApply={onFilterApply}
      orgTypePrefix={orgTypePrefix}
      setIsFiltersBarVisible={setIsFiltersBarVisible}
      isFiltersBarVisible={isFiltersBarVisible}
      isApplyFilter={isApplyFilter}
      onFilterReset={onFilterReset}
      geoTypePrefix={geoTypePrefix}
      naceCodeTreeData={naceCodeTreeData}
      isWithSearch={isWithSearch}
      isLoading={isLoading}
      isLazyOwnOperationExampleLoading={isLazyOwnOperationExampleLoading}
      onShowArchiveChange={onShowArchiveChange}
      onOpenRestrictedModal={onOpenRestrictedModal}
      activeRestrictedModal={activeRestrictedModal}
      archiveError={archiveError as IArchiveError || deArchiveError as IArchiveError || deleteError as IArchiveError}
      locationsCountries={locationsCountries?.data || []}
      onDeleteRecord={onDeleteRecord}
      isDeleteModalVisible={isDeleteModalVisible}
      onCloseDeleteModal={onCloseDeleteModal}
      onDeleteConfirm={onDeleteConfirm}
      isImportErrorModalVisible={isImportErrorModalVisible}
      importErrorMessage={importErrorMessage}
      onCloseImportErrorModal={handleCloseImportErrorModal}
      onImportError={handleImportError}
    />
  );
}

export default OwnOperations;
